import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Nav from "../comps/nav";
import Footer from "../comps/footer";

interface PatientData {
    profile_last_updated: string;
    name: string;
    email: string;
    height: string;
    weight: string;
    dob: string;
    gender: string;
    medicalNotes: string;
    primaryLanguage: string;
    allergies: string | string[]; // accept string or array
    bloodyType: string;
    emergencyContact: {
        name: string;
        relationship: string;
        phone: string;
    };
    medications_last_updated: string;
    medications: {
        active: string[];
        inactive: string[];
    }
}

export default function PatientInfo(){
    const [patientInfo, setPatientInfo] = useState<PatientData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token) {
            console.warn("[PatientInfo] no token provided in query string; skipping fetch");
            setError("No token provided");
            return;
        }

        const fetchPatientInfo = async () => {
            setLoading(true);
            setError(null);
            try {
                // console.log("[PatientInfo] fetching");
                const url = `${process.env.REACT_APP_API_URL}/qr/decode_token?token=${encodeURIComponent(token)}`;
                // console.log("[PatientInfo] url:", url);

                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Accept": "application/json"
                    }
                });

                // console.log("[PatientInfo] status:", response.status);
                const contentType = response.headers.get("content-type") || "";
                // console.log("[PatientInfo] content-type:", contentType);

                if (!response.ok) {
                    const text = await response.text();
                    // console.error("[PatientInfo] non-ok response body:", text);
                    setError(`Server returned ${response.status}`);
                    setLoading(false);
                    return;
                }

                if (contentType.includes("application/json")) {
                    const data = await response.json();
                    // backend may wrap the object in `data` or return directly
                    const payload = (data && data.data) ? data.data : data;
                    setPatientInfo(payload ?? null);
                } else {
                    const text = await response.text();
                    console.warn("[PatientInfo] expected JSON but got:", text);
                    setError("Unexpected response format from server");
                }
            } catch (e: any) {
                // console.error("[PatientInfo] Error fetching patient info:", e);
                setError(e?.message ?? String(e));
            } finally {
                setLoading(false);
            }
        };

        fetchPatientInfo();
    }, [location.search]);

    const formatDate = (iso?: string) => {
        if (!iso) return "Unknown";
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    return (
        <main className="min-h-screen bg-[#f2f4f5] pb-20">
            <Nav/>
            <section className="relative px-4 pt-12 pb-12">
                <div className="mx-auto max-w-3xl text-gray-800">
                    <h1 className="text-2xl font-semibold">Patient Information</h1>

                    {loading && (
                        <div className="mt-4">Loading patient data…</div>
                    )}

                    {error && (
                        <div className="mt-4 text-red-600">Error: {error}</div>
                    )}

                    {!loading && !error && !patientInfo && (
                        <div className="mt-4 text-gray-600">No patient data available.</div>
                    )}

                    {patientInfo && (
                        <div className="mt-6 space-y-6">
                            {/* Primary header: name + key identifiers */}
                            <div>
                                <h2 className="text-xl font-semibold">{patientInfo.name || "Unnamed Patient"}</h2>
                                <div className="mt-2 text-sm text-gray-500">
                                    DOB: {patientInfo.dob || "Unknown"} · Gender: {patientInfo.gender || "Unknown"} · Blood type: {patientInfo.bloodyType || "Unknown"}
                                </div>
                            </div>

                            {/* Allergies (high priority) */}
                            <section>
                                <h3 className="text-lg font-medium">Allergies</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    Allergies: {patientInfo.allergies}
                                </div>
                            </section>

                            {/* Active medications */}
                            <section>
                                <h3 className="text-lg font-medium">Active Medications</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    {patientInfo.medications?.active && patientInfo.medications.active.length > 0 ? (
                                        <ul className="space-y-2">
                                            {patientInfo.medications.active.map((m, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <span className="inline-block w-2 h-2 mt-2 rounded-full bg-blue-500" />
                                                    <div className="text-sm">{m}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-sm text-gray-500">No active medications listed</div>
                                    )}
                                </div>
                            </section>

                            {/* Emergency contact */}
                            <section>
                                <h3 className="text-lg font-medium">Emergency Contact</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    {patientInfo.emergencyContact ? (
                                        <div className="text-sm space-y-1">
                                            <div><span className="font-medium">Name:</span> {patientInfo.emergencyContact.name}</div>
                                            <div><span className="font-medium">Relationship:</span> {patientInfo.emergencyContact.relationship}</div>
                                            <div><span className="font-medium">Phone:</span> {patientInfo.emergencyContact.phone}</div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-gray-500">No emergency contact listed</div>
                                    )}
                                </div>
                            </section>

                            {/* Medical notes and additional info */}
                            <section>
                                <h3 className="text-lg font-medium">Medical Notes</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    <div className="whitespace-pre-wrap text-sm">{patientInfo.medicalNotes || "No notes available"}</div>
                                </div>
                            </section>

                            {/* Vitals / basics */}
                            <section>
                                <h3 className="text-lg font-medium">Vitals & Basics</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800 grid grid-cols-2 gap-4 text-sm">
                                    <div><span className="font-medium">Height:</span> {patientInfo.height || "—"}</div>
                                    <div><span className="font-medium">Weight:</span> {patientInfo.weight || "—"}</div>
                                    <div><span className="font-medium">Primary language:</span> {patientInfo.primaryLanguage || "—"}</div>
                                    <div><span className="font-medium">Email:</span> {patientInfo.email || "—"}</div>
                                </div>
                            </section>

                            {/* Metadata and timestamps */}
                            <section>
                                <h3 className="text-lg font-medium">Timestamps</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800 text-sm">
                                    <div><span className="font-medium">Profile last updated:</span> {formatDate(patientInfo.profile_last_updated)}</div>
                                    <div className="mt-1"><span className="font-medium">Medications last updated:</span> {formatDate(patientInfo.medications_last_updated)}</div>
                                </div>
                            </section>

                            {/* Inactive medications */}
                            <section>
                                <h3 className="text-lg font-medium">Inactive Medications</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    {patientInfo.medications?.inactive && patientInfo.medications.inactive.length > 0 ? (
                                        <ul className="space-y-1 text-sm">
                                            {patientInfo.medications.inactive.map((m, i) => (
                                                <li key={i}>{m}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="text-sm text-gray-500">No inactive medications listed</div>
                                    )}
                                </div>
                            </section>

                            {/* Raw JSON for debugging (collapsible) */}
                            <section>
                                <h3 className="text-lg font-medium">Raw Data</h3>
                                <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm text-gray-800">
                                    <details>
                                        <summary className="cursor-pointer text-sm text-blue-600">Show raw JSON</summary>
                                        <pre className="mt-2 overflow-x-auto text-xs text-gray-700">{JSON.stringify(patientInfo, null, 2)}</pre>
                                    </details>
                                </div>
                            </section>
                        </div>
                    )}
                </div>
            </section>
            <Footer/>
        </main>
    )
}
